import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  background: var(--bg-primary);
  border-radius: 16px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  @keyframes slideIn {
    from { opacity: 0; transform: translateY(-20px) scale(0.95); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 900;
  color: var(--accent-orange);
  margin-bottom: 1.5rem;
  font-family: 'Poppins', sans-serif;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-gray);
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: var(--bg-sidebar);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--accent-orange);
`;

const CheckboxLabel = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button<{ $variant?: 'secondary' }>`
  padding: 0.6rem 1.25rem;
  background: ${props => props.$variant === 'secondary' ? 'transparent' : 'var(--accent-orange)'};
  color: ${props => props.$variant === 'secondary' ? 'var(--text-gray)' : 'white'};
  border: ${props => props.$variant === 'secondary' ? '2px solid var(--card-border)' : 'none'};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$variant === 'secondary' ? 'var(--bg-sidebar)' : 'var(--accent-orange-dark)'};
    transform: translateY(-2px);
    box-shadow: ${props => props.$variant === 'secondary' ? 'none' : '0 4px 12px rgba(255, 150, 0, 0.3)'};
  }
`;

const JUMPER_AVAILABLE_METRICS = [
  { value: 'totalJumps', label: 'Total Jumps' },
  { value: 'averageDistance', label: 'Average Distance' },
  { value: 'bestDistance', label: 'Best Distance' },
  { value: 'totalSessions', label: 'Training Sessions' },
  { value: 'totalWins', label: 'Total Wins' },
];

export interface JumperDashboardSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  topMetrics: string[];
  onSave: (newTopMetrics: string[]) => void;
}

const JumperDashboardSettings: React.FC<JumperDashboardSettingsProps> = ({
  isOpen,
  onClose,
  topMetrics,
  onSave,
}) => {
  const [selectedTopMetrics, setSelectedTopMetrics] = useState<string[]>(topMetrics);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedTopMetrics(topMetrics);
    }
  }, [isOpen, topMetrics]);

  const handleMetricToggle = (value: string) => {
    setSelectedTopMetrics(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      }
      if (prev.length >= 4) return prev;
      return [...prev, value];
    });
  };

  const handleSave = () => {
    if (selectedTopMetrics.length === 0) {
      alert('Please select at least one metric');
      return;
    }
    if (selectedTopMetrics.length > 4) {
      alert('Please select at most 4 metrics');
      return;
    }
    onSave(selectedTopMetrics);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Title>Dashboard Settings</Title>
        <Section>
          <SectionTitle>Top Metrics (Select up to 4)</SectionTitle>
          <CheckboxList>
            {JUMPER_AVAILABLE_METRICS.map(metric => (
              <CheckboxItem key={metric.value}>
                <Checkbox
                  type="checkbox"
                  checked={selectedTopMetrics.includes(metric.value)}
                  onChange={() => handleMetricToggle(metric.value)}
                  disabled={!selectedTopMetrics.includes(metric.value) && selectedTopMetrics.length >= 4}
                />
                <CheckboxLabel>{metric.label}</CheckboxLabel>
              </CheckboxItem>
            ))}
          </CheckboxList>
        </Section>
        <ButtonGroup>
          <Button $variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save
          </Button>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
};

export default JumperDashboardSettings;
